import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface PaycheckArticles {
    [key: string]: {
        articleTypeId: number
        qty: number
    }
}

export interface PaycheckItem {
    paycheckIds: { [key: string]: number }
    month: string // "YYYY-MM-DD"
    total: number
    totalDetails: any
    articles: PaycheckArticles
}

export interface PaycheckListData {
    paychecks: PaycheckItem[]
    total: number
    totalDetails: any
    articles: { [key: string]: number }
    currencyId: number
}

export interface PaycheckListResponse {
    statusCode: number
    success: boolean
    message: string
    data?: PaycheckListData
}

export interface PaycheckDetailsResponse {
    statusCode: number
    success: boolean
    message: string
    data?: any
}

export interface PaycheckDownloadResponse {
    statusCode: number
    success: boolean
    message: string
    data?: {
        downloadUrl: string
    }
}

export const paycheckApi = createApi({
    reducerPath: 'paycheckApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.ENV === 'local' ? `${process.env.API_URL}/api` : `${window.location.origin}/api`,
        prepareHeaders: (headers) => {
            const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
            if (token) headers.set('X-CSRF-TOKEN', token)
            headers.set('Content-Type', 'application/json')
            headers.set('Response-V2', '1')
            return headers
        },
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
    }),
    endpoints: (builder) => ({
        getPaycheckList: builder.query<PaycheckListResponse, { fromDate: string; toDate: string }>({
            query: ({ fromDate, toDate }) => ({
                url: '/employee/paycheck',
                method: 'GET',
                params: { fromDate, toDate },
            }),
        }),
        getPaycheckDetails: builder.mutation<PaycheckDetailsResponse, { ids: number[] }>({
            query: (body) => ({
                url: '/employee/paycheck/details',
                method: 'POST',
                body,
            }),
        }),
        downloadPaycheckPdf: builder.mutation<PaycheckDownloadResponse, { payrollId: number }>({
            query: ({ payrollId }) => ({
                url: `/employee/paycheck/${payrollId}/pdf`,
                method: 'POST',
            }),
        }),
        downloadPaycheckXlsx: builder.mutation<PaycheckDownloadResponse, { payrollId: number }>({
            query: ({ payrollId }) => ({
                url: `/employee/paycheck/${payrollId}/xlsx`,
                method: 'POST',
            }),
        }),
        downloadPaycheckPdfBulk: builder.mutation<PaycheckDownloadResponse, { ids: number[] }>({
            query: (body) => ({
                url: '/employee/paycheck/pdf',
                method: 'POST',
                body,
            }),
        }),
        downloadPaycheckXlsxBulk: builder.mutation<PaycheckDownloadResponse, { ids: number[] }>({
            query: (body) => ({
                url: '/employee/paycheck/xlsx',
                method: 'POST',
                body,
            }),
        }),
    }),
})

export const {
    useGetPaycheckListQuery,
    useGetPaycheckDetailsMutation,
    useDownloadPaycheckPdfMutation,
    useDownloadPaycheckXlsxMutation,
    useDownloadPaycheckPdfBulkMutation,
    useDownloadPaycheckXlsxBulkMutation,
} = paycheckApi
